import { defineComponent as _defineComponent } from 'vue'
import ProductSpecificationViewModel = Vertica.LouisPoulsen.Application.ProductConfigurator.ViewModels.ProductSpecificationViewModel;
import { store } from '@/store';
import ProfessionalVariantViewObject = Vertica.LouisPoulsen.Application.Content.Pages.Product.ProfessionalVariantViewObject;
import VueProgrammaticInvisibleGoogleRecaptcha from 'vue-programmatic-invisible-google-recaptcha';
import GetSpecificationQuoteWebRequest = Vertica.LouisPoulsen.Application.Commerce.Requests.GetSpecificationQuoteWebRequest;
import ScrollService from '@/core/scroll.service';
import ProductSpecificationPriceViewModel = Vertica.LouisPoulsen.Application.ProductConfigurator.ViewModels.ProductSpecificationPriceViewModel;
import CopyToClipBoardButton from '@/ui/shared/CopyToClipBoardButton.vue';
import Http from '@/core/http/http.service';
import DownloadTenderText from '@/ui/views/product-details/variant-download/DownloadTenderText.vue';
import ProductSpecificationPriceViewObject = Vertica.LouisPoulsen.Application.ProductConfigurator.ViewModels.ProductSpecificationPriceViewObject;
import { computed, onMounted, ref, watch } from 'vue';
import { useValidator } from '@/core/app.service';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductSpecification',
  props: {
    model: null
  },
  setup(__props: any) {

const props = __props as {model: ProductSpecificationViewModel}

const contactForm = ref<HTMLElement>(null);


const recaptchaKey = computed<string>(() => store.getters['app/recaptchaKey']);
const isB2BAuthenticated = computed<boolean>(() => store.getters['app/isB2BAuthenticated']);

const productVariants = computed<ProfessionalVariantViewObject[]>(() => store.getters['catalog/productVariants']);

function sendSpecificationQuote(request: GetSpecificationQuoteWebRequest) : Promise<any> {
    return store.dispatch('catalog/sendSpecificationQuote', request);
}

function selectVariantConfig({ variant, pushToHistory } : {variant:VariantBaseViewObject, pushToHistory?:boolean}) {
    store.dispatch('catalog/selectVariantConfig', { variant, pushToHistory });
}

function toggleShowVariantDataSpecs(flag: boolean) {
    store.dispatch('catalog/toggleShowVariantDataSpecs', flag);
}

function getQuoteCountries() : Promise<any> {
    return store.dispatch('catalog/getQuoteCountries');
}

function getCountryStates(countryCode: string) : Promise<any> {
    return store.dispatch('catalog/getCountryStates', countryCode);
}

function getProfessions() : Promise<any> {
    return store.dispatch('catalog/getProfessions');
}

const prices = ref<ProductSpecificationPriceViewObject[]>([]);

const isSending = ref<boolean>(false);

const countries = ref([]);
const states = ref([]);
const professions = ref([]);

const contact = ref<any & {country: Vertica.LouisPoulsen.Application.Content.ViewModels.CountryViewModel}>({
    name: '',
    profession: {
        name: '',
        id: ''
    },
    company: '',
    phone: '',
    email: '',
    postalCode: '',
    city: '',
    country: {
        countryCode: '',
        hasState: false,
        name: ''
    },
    state: '',
    comment: '',
    code: ''
});

const currentUrl = computed<string>(() => window.location.href);
const urlText = computed<string>(() => window.location.href.replace(window.location.protocol, '').replace('//', ''));

onMounted(() => {
    if (isB2BAuthenticated.value) {
        loadPrices();
    }

    if (countries.value.length === 0) {
        getQuoteCountries().then(data => {
            countries.value = data;
        });
    }
    if (professions.value.length === 0) {
        getProfessions().then(data => {
            professions.value = data;
        });
    }
});

watch(() => contact.value.country, getStates);
function getStates(): void {
    if (contact.value.country.hasState) {
        getCountryStates(contact.value.country.countryCode).then(data => {
            states.value = data;
        });
    } else {
        states.value = [];
    }
}

function hasHexCode(spec: any): boolean {
    return !!spec.colorHexCode;
}

function getHexCode(spec: any): string {
    return spec.colorHexCode;
}

function downloadPdf(): void {
    window.open(props.model.pdfDownloadLink);
}

function downloadTenderTexts(): void {
    window.open(props.model.tenderTextDownloadLink);
}

function openVariant(variantId: string): void {
    const variant = productVariants.value.filter((x) => x.id === variantId);
    if (variant.length) {
        selectVariantConfig({ variant: variant[0], pushToHistory: true });
        toggleShowVariantDataSpecs(true);
    }
}

const _uid = Math.floor(Math.random() * 1000000).toString();
const componentId = 'productSpecification';
const refs = { [componentId + _uid]: ref(null) };

function setRecaptchaRef(el: any) {
    setTimeout(() => {
        refs[componentId + _uid].value = el;
    }, 100);
}

const $validator = useValidator();

function submit(): void {
    if (!isSending.value) {
        $validator
            .validateAll()
            .then(isValid => {
                if (isValid) {
                    const recaptchaComponent = refs[componentId + _uid].value as any;
                    recaptchaComponent.execute();
                } else {
                    scrollInvalidElementIntoView();
                }
            })
            .catch(x => {
                isSending.value = false;
                console.log('catch' + x);
            });
    }
}

function submitForm(recaptchaToken): void {
    isSending.value = true;
    const request: GetSpecificationQuoteWebRequest = {
        name: contact.value.name,
        profession: contact.value.profession.name,
        company: contact.value.company,
        phone: contact.value.phone,
        email: contact.value.email,
        postalCode: contact.value.postalCode,
        city: contact.value.city,
        countryCode: contact.value.country.countryCode,
        state: contact.value.state.englishName,
        comments: contact.value.comment,
        code: props.model.configurationCode,
        recaptchaToken
    };

    sendSpecificationQuote(request).then(() => {
        isSending.value = false;
    }).catch(() => {
        isSending.value = false;
    });
}

async function loadPrices(): Promise<void> {
    const priceModel = await Http.get<ProductSpecificationPriceViewModel>('/productconfigurator/prices', { code: props.model.configurationCode });
    prices.value = priceModel.prices;
}

function scrollToContact(): void {
    ScrollService.scrollToElement(contactForm.value, 0, 300);
}

function scrollInvalidElementIntoView(): void {
    const firstInvalidElement = document.querySelector('.is-danger');
    if (firstInvalidElement) {
        firstInvalidElement.scrollIntoView({ behavior: 'smooth' });
    }
}

return { __sfc: true,contactForm, props, recaptchaKey, isB2BAuthenticated, productVariants, sendSpecificationQuote, selectVariantConfig, toggleShowVariantDataSpecs, getQuoteCountries, getCountryStates, getProfessions, prices, isSending, countries, states, professions, contact, currentUrl, urlText, getStates, hasHexCode, getHexCode, downloadPdf, downloadTenderTexts, openVariant, _uid, componentId, refs, setRecaptchaRef, $validator, submit, submitForm, loadPrices, scrollToContact, scrollInvalidElementIntoView, VueProgrammaticInvisibleGoogleRecaptcha, CopyToClipBoardButton, DownloadTenderText }
}

})